import { Container } from '@mantine/core';
import type { QueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { getLogger } from 'loglevel';
import { useCallback, useMemo } from 'react';

import { getFirstInvalidStep } from '@apple/channel/routes/_authed/-steps/validation';
import { useTranslation } from '@apple/lib/i18next';
import { useBreakpoints } from '@apple/ui/layouts/hooks/useBreakpoints';
import { useLogger } from '@apple/utils/logging/useLogger';

import { useCartId } from '@apple/features/cart/contexts/cart-id';
import type { CartId } from '@apple/features/cart/models/cart-id';
import { setLocationSelections } from '@apple/features/cart-channel/api';
import { orderSubmissionFormActions } from '@apple/features/cart-channel/components/OrderOptions/form-actions';
import { useSelectedQuantities } from '@apple/features/cart-channel/hooks/useSelectedQuantities';
import { useSelectedShippingAddress } from '@apple/features/cart-channel/hooks/useSelectedShippingAddress';
import { useSubmitCart } from '@apple/features/cart-channel/hooks/useSubmitCart';
import type { OrderSubmissionRequest } from '@apple/features/cart-channel/models/submission';
import { cartQueryOptions } from '@apple/features/cart-channel/queries';
import { locationQueries } from '@apple/features/location/queries/location-listing.queries';

import { ShopSteps } from './-steps/ShopSteps';
import { stepNameSchema, stepRouteParamsSchema } from './-steps/types';

const log = getLogger('cart');

export const Route = createFileRoute('/_authed/cart/$step')({
	params: {
		parse: x => stepRouteParamsSchema.parse(x),
		stringify: ({ step }) => ({ step: `${step}` }),
	},

	beforeLoad: async args => {
		const { bulkOrderId: cartId } = await args.context.queryClient.fetchQuery(
			cartQueryOptions.cartId(true),
		);
		const firstInvalidStep = await getFirstInvalidStep({
			cartId,
			queryClient: args.context.queryClient,
		});

		if (firstInvalidStep === 'locations' && !args.context.auth.canAccessMultipleLocations) {
			await addSingleLocation(args.context.queryClient, cartId);
			throw redirect({
				to: '/cart/$step',
				params: {
					step: 'products',
				},
			});
		}

		const indexOfCurrentStep = stepNameSchema.options.indexOf(args.params.step);
		const indexOfFirstInvalidStep = stepNameSchema.options.indexOf(firstInvalidStep);

		if (indexOfCurrentStep > indexOfFirstInvalidStep) {
			// Redirect to the first invalid step if it is not the requested step
			throw redirect({
				to: '/cart/$step',
				params: {
					step: firstInvalidStep,
				},
			});
		}

		return {
			stepName: args.params.step,
		};
	},
	component: CartStepRoute,
});

function CartStepRoute() {
	const { t } = useTranslation('shop');
	const { auth, stepName } = Route.useRouteContext();
	const navigate = Route.useNavigate();
	const cartId = useCartId();
	const quantitySelections = useSelectedQuantities();
	const shippingAddressesSelections = useSelectedShippingAddress();
	const submission = useSubmitCart();
	const { smOrSmaller, mdOrSmaller } = useBreakpoints();

	const everyLocationWithQuantitiesHasShippingAddress = useMemo(
		() =>
			quantitySelections.locationsWithQuantities.every(x =>
				shippingAddressesSelections.query.data?.some(y => x.locationId == y.shipToKey),
			) && (shippingAddressesSelections.query.data?.length ?? 0) > 0,
		[shippingAddressesSelections.query.data, quantitySelections.locationsWithQuantities],
	);

	const canBeSubmitted = useMemo(() => {
		return (
			quantitySelections.locationsWithQuantities.length > 0 &&
			everyLocationWithQuantitiesHasShippingAddress
		);
	}, [quantitySelections.locationsWithQuantities, everyLocationWithQuantitiesHasShippingAddress]);

	useLogger({
		name: 'CartStepRoute',
		props: [{ auth, stepName, canBeSubmitted, everyLocationWithQuantitiesHasShippingAddress }],
		log,
	});

	const invalidItems = useMemo(
		() => quantitySelections.locationsWithQuantities.length <= 0,
		[quantitySelections.locationsWithQuantities],
	);

	const handleSubmit = useCallback(
		async (request: OrderSubmissionRequest) => {
			const invalidShippingAddress = !everyLocationWithQuantitiesHasShippingAddress;

			if (!canBeSubmitted || invalidItems || invalidShippingAddress) {
				log.warn('Cannot submit order:', {
					canBeSubmitted,
					invalidItems,
					invalidShippingAddress,
				});
				return;
			}

			// Mutate resets the cartId so we need to store it before calling it
			const submittedCartId = cartId;

			log.debug('Submitting order:', { submittedCartId, request });

			await submission.mutation.mutateAsync(
				{
					orderReason: request.shopOrderType ?? '',
					options: {
						holdUntilDate: request.shopOrderDate,
						isGhostShipment: request.ghostShipment,
						orderComments: request.shopOrderComments,
						orderName: request.shopOrderName,
						orderNumber: request.shopPurchaseOrderNumber,
						userAcknowledgedApprovalRequired: !!request.shopOrderComments,
					},
					onCommentRequiredError() {
						orderSubmissionFormActions.setFieldError(
							'shopOrderComments',
							t('error.justificationRequired'),
						);
					},
				},
				{
					onSuccess: () =>
						void navigate({
							to: '/cart/$cartId/confirmation',
							params: { cartId: submittedCartId },
							resetScroll: true,
						}),
					onError: () => {
						// Scroll to the top of the page to ensure that the user sees the error message.
						window.scrollTo({ top: 0, behavior: 'smooth' });
					},
				},
			);
		},
		[
			canBeSubmitted,
			cartId,
			submission.mutation,
			invalidItems,
			everyLocationWithQuantitiesHasShippingAddress,
			t,
			navigate,
		],
	);

	return (
		<Container size='xl' p='md'>
			<ShopSteps
				currentStepIndex={
					stepNameSchema.options.indexOf(stepName) -
					(auth.canAccessMultipleLocations ? 0 : 1)
				}
				locationStepVisible={auth.canAccessMultipleLocations}
				stepperVerticalOrientation={smOrSmaller}
				hideDescription={mdOrSmaller}
				onGotoStep={step => {
					const stepName = stepNameSchema.options.at(
						step + (auth.canAccessMultipleLocations ? 0 : 1),
					);
					void navigate({ to: '/cart/$step', params: { step: stepName } });
				}}
				invalidItems={invalidItems}
				invalidShippingAddress={!everyLocationWithQuantitiesHasShippingAddress}
				submissionPending={submission.mutation.isPending}
				onSubmit={handleSubmit}
			/>
		</Container>
	);
}

async function addSingleLocation(queryClient: QueryClient, cartId: CartId) {
	const locations = await queryClient.fetchQuery(locationQueries.getLocations({}));
	if (locations.locations.items.length !== 1) {
		log.error(
			'Expected exactly one location to be available, but found:',
			locations.locations.items,
		);
		return;
	}

	const location = locations.locations.items.at(0)!;
	await setLocationSelections({
		bulkOrderId: cartId,
		isSelected: true,
		locationSelection: {
			locationId: location.id,
			programs: location.programs.map(x => x.customerGroupId),
		},
	});
}
